// API service for communicating with FastAPI backend
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User,
  UserWithBatches,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  Conversation,
  ConversationWithMessages,
  CreateConversationRequest,
  Message,
  CreateMessageRequest,
  APIError,
  Invitation,
  InvitationAcceptRequest
} from '../types';
// import logger from '@/utils/logger';

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    // Use production backend URL as fallback for deployed environments
    // Priority: window.__API_URL__ (runtime) > VITE_API_URL (build-time) > fallback
    // Wait a tick to ensure window.__API_URL__ is set by injected script
    const getBaseURL = () => {
      // Check multiple times as script injection might be async
      if ((window as any).__API_URL__) {
        return (window as any).__API_URL__;
      }
      if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL;
      }
      if (window.location.hostname.includes('run.app')) {
        return 'https://backend-real-auth-344710332118.us-central1.run.app';
      }
      return 'http://localhost:8000';
    };
    
    const baseURL = getBaseURL();
    
    // Reduced logging - only log in development
    if (import.meta.env.DEV) {
      console.log(`🔧 API Service initialized: ${baseURL}`);
    }
    
    // Re-check after a delay to see if URL was injected
    setTimeout(() => {
      if ((window as any).__API_URL__ && (window as any).__API_URL__ !== baseURL) {
        console.warn(`⚠️ API URL changed after initialization: ${baseURL} -> ${(window as any).__API_URL__}`);
        this.api.defaults.baseURL = (window as any).__API_URL__;
      }
    }, 100);
    
    this.api = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load token from localStorage
    this.token = localStorage.getItem('access_token');
    if (this.token) {
      this.setAuthHeader(this.token);
    }

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Only log in development mode to reduce console spam
        if (import.meta.env.DEV) {
          console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        }
        return config;
      },
      (error) => {
        console.error('❌ Request error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Only log errors or in development mode
        if (import.meta.env.DEV || response.status >= 400) {
          console.log(`✅ API Response: ${response.status} ${response.config.url}`);
        }
        return response;
      },
      (error) => {
        console.error('❌ Response error:', error);
        console.error('❌ Error details:', {
          message: error.message,
          response: error.response,
          responseData: error.response?.data,
          status: error.response?.status,
          code: error.code,
        });
        if (error.response?.status === 401) {
          this.clearAuth();
          window.location.href = '/login';
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: any): APIError {
    if (error.response) {
      return {
        detail: error.response.data?.detail || 'An error occurred',
        status_code: error.response.status,
      };
    } else if (error.request) {
      return {
        detail: 'Network error - could not connect to server',
        status_code: 0,
      };
    } else {
      return {
        detail: error.message || 'An unexpected error occurred',
        status_code: 0,
      };
    }
  }

  private setAuthHeader(token: string) {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  private clearAuth() {
    this.token = null;
    localStorage.removeItem('access_token');
    delete this.api.defaults.headers.common['Authorization'];
  }

  // Generic HTTP methods for extensions
  async get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    // Use standard OAuth2 token endpoint
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await this.api.post<AuthResponse>('/api/auth/token', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    const { access_token } = response.data;
    this.token = access_token;
    localStorage.setItem('access_token', access_token);
    this.setAuthHeader(access_token);

    return response.data;
  }

  async register(userData: RegisterRequest): Promise<User> {
    const response = await this.api.post<User>('/api/auth/register', userData);
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<User>('/api/auth/me');
    return response.data;
  }

  logout() {
    this.clearAuth();
  }

  // Conversation methods
  async getConversations(limit = 50, offset = 0): Promise<Conversation[]> {
    const response = await this.api.get<Conversation[]>('/api/conversations/', {
      params: { limit, offset },
    });
    return response.data;
  }

  async createConversation(data: CreateConversationRequest): Promise<Conversation> {
    const response = await this.api.post<Conversation>('/api/conversations/', data);
    return response.data;
  }

  async getConversation(id: string): Promise<ConversationWithMessages> {
    const response = await this.api.get<ConversationWithMessages>(`/api/conversations/${id}`);
    return response.data;
  }

  async updateConversation(id: string, data: { title: string }): Promise<Conversation> {
    const response = await this.api.put<Conversation>(`/api/conversations/${id}`, data);
    return response.data;
  }

  async deleteConversation(id: string): Promise<void> {
    await this.api.delete(`/api/conversations/${id}`);
  }

  async clearAllConversations(): Promise<{ message: string; deleted_count: number }> {
    const response = await this.api.delete<{ message: string; deleted_count: number }>('/api/conversations/');
    return response.data;
  }

  // Message methods
  async getMessages(conversationId: string, limit = 100, offset = 0): Promise<Message[]> {
    const response = await this.api.get<Message[]>(`/api/messages/conversation/${conversationId}`, {
      params: { limit, offset },
    });
    return response.data;
  }

  async createMessage(data: CreateMessageRequest): Promise<Message> {
    const response = await this.api.post<Message>('/api/messages/', data);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; database: string; openai: string }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Get current auth token
  getToken(): string | null {
    return this.token;
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    return !!this.token;
  }

  async acceptInvitation(request: InvitationAcceptRequest): Promise<User> {
    const response = await this.api.post<User>('/api/auth/accept-invitation', request);
    return response.data;
  }

  async forgotPassword(email?: string, username?: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post<{ success: boolean; message: string }>('/api/auth/forgot-password', {
      email,
      username,
    });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post<{ success: boolean; message: string }>('/api/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.put<{ success: boolean; message: string }>('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  }

  // Admin methods
  async listUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    is_active?: boolean;
  }): Promise<UserWithBatches[]> {
    const response = await this.api.get<UserWithBatches[]>('/api/admin/users', { params });
    return response.data;
  }

  async getUser(userId: string): Promise<UserWithBatches> {
    const response = await this.api.get<UserWithBatches>(`/api/admin/users/${userId}`);
    return response.data;
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<UserWithBatches> {
    const response = await this.api.put<UserWithBatches>(`/api/admin/users/${userId}/activate`, {
      is_active: isActive,
    });
    return response.data;
  }

  async deleteUser(userId: string): Promise<void> {
    await this.api.delete(`/api/admin/users/${userId}`);
  }

  async resetUserPassword(userId: string): Promise<{ success: boolean; message: string; token?: string; expires_at?: string }> {
    const response = await this.api.post<{ success: boolean; message: string; token?: string; expires_at?: string }>(`/api/admin/users/${userId}/reset-password`);
    return response.data;
  }

  async updateUserRole(userId: string, role: 'user' | 'admin'): Promise<UserWithBatches> {
    const response = await this.api.put<UserWithBatches>(`/api/admin/users/${userId}/role`, {
      role,
    });
    return response.data;
  }

  // Invitation methods
  async createInvitation(email: string): Promise<Invitation> {
    const response = await this.api.post<Invitation>('/api/admin/invitations', { email });
    return response.data;
  }

  async listInvitations(statusFilter?: string): Promise<Invitation[]> {
    const params = statusFilter ? { status: statusFilter } : {};
    const response = await this.api.get<Invitation[]>('/api/admin/invitations', { params });
    return response.data;
  }

  async cancelInvitation(invitationId: string): Promise<void> {
    await this.api.delete(`/api/admin/invitations/${invitationId}`);
  }

  async resendInvitation(invitationId: string): Promise<Invitation> {
    const response = await this.api.post<Invitation>(`/api/admin/invitations/${invitationId}/resend`);
    return response.data;
  }

  async deleteInvitation(invitationId: string): Promise<void> {
    await this.api.delete(`/api/admin/invitations/${invitationId}/permanent`);
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
